import { useTranslation } from 'react-i18next';
import { object, string, lazy } from 'yup';
import clsx from 'clsx';
import { FormConfig } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/types';
import { Box, Button, makeStyles } from '@dxb-web-ui-framework/design-system';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import React from 'react';
import { ECOMMBXLoader } from '../../../../../content-composer/src/components/Generic/ECOMMBXLoader/ECOMMBXLoader';
import { dataToAutocompleteOptions } from '../../../utils';
import { UnknownBankDetails, ValueIdentifier } from '../../types';
import { SwiftBank, SwiftCity, SwiftCountry } from '../../../domain/Transfer/entities';

const useStyles = makeStyles(theme => ({
  submitButton: {
    [theme.breakpoints.down('sm')]: {
      marginTop: 'auto !important',
      marginBottom: theme.spacing(4),
    },
  },
  marginButton: {
    marginTop: theme.spacing(5),
  },
  loaderContainer: {
    width: '100%',
    marginTop: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      flex: 1,
      alignItems: 'center',
    },
  },
}));
const useBicSwiftsConfig = (
  isBankCountry: boolean,
  isBankCity: boolean,
  isLoadingSwiftCodes: boolean,
  isLoadingSwiftCountries: boolean,
  isLoadingSwiftCities: boolean,
  isLoadingSwiftBanks: boolean,
  onChangeHandler: Function,
  unknownBankDetails: Nullable<UnknownBankDetails>,
  countriesData?: SwiftCountry[],
  swiftCitiesData?: SwiftCity[],
  swiftBanksData?: SwiftBank[],
): FormConfig => {
  const countriesOptions = dataToAutocompleteOptions(countriesData);
  const swiftCitiesOptions = dataToAutocompleteOptions(swiftCitiesData?.filter(c => c.cityName !== ''));
  const bankNamesOptions = dataToAutocompleteOptions(swiftBanksData?.filter(c => c.bankName !== ''));
  const classes = useStyles();
  const { t } = useTranslation();

  return {
    validationSchema: object({
      [ValueIdentifier.BANK_COUNTRY]: lazy(value => {
        switch (typeof value) {
          case 'object':
            return object().nullable(true).required(t('fundsTransferPage.Forms.GenericDrawer.Validations.Country'));
          default:
            return string().required(t('fundsTransferPage.Forms.GenericDrawer.Validations.Country'));
        }
      }),
      [ValueIdentifier.BANK_CITY]: lazy(value => {
        switch (typeof value) {
          case 'object':
            return object().nullable(true).required(t('fundsTransferPage.Forms.GenericDrawer.Validations.City'));
          default:
            return string().required(t('fundsTransferPage.Forms.GenericDrawer.Validations.City'));
        }
      }),
      [ValueIdentifier.BANK_NAME]: lazy(value => {
        switch (typeof value) {
          case 'object':
            return object().nullable(true).required(t('fundsTransferPage.Forms.GenericDrawer.Validations.Bank'));
          default:
            return string().required(t('fundsTransferPage.Forms.GenericDrawer.Validations.Bank'));
        }
      }),
    }),
    components: [
      {
        label: t('fundsTransferPage.Forms.GenericDrawer.Country'),
        key: ValueIdentifier.BANK_COUNTRY,
        type: 'autocomplete',
        options: countriesOptions,
        defaultValue: null,
        externalOnChangeHandler: onChangeHandler,
        disabled: isLoadingSwiftCodes,
        loading: isLoadingSwiftCountries,
        loadingText: t('fundsTransferPage.Forms.GenericDrawer.LoadingText.Country'),
        properties: {
          forcePopupIcons: true,
          mt: 3,
          noOptionsText: t('fundsTransferPage.Forms.AddBankDetails.NoOptions.Message'),
        },
      },
      {
        label: t('fundsTransferPage.Forms.GenericDrawer.City'),
        key: ValueIdentifier.BANK_CITY,
        type: 'autocomplete',
        options: swiftCitiesOptions,
        defaultValue: null,
        externalOnChangeHandler: onChangeHandler,
        disabled: isLoadingSwiftCodes || !isBankCountry,
        loading: isLoadingSwiftCities,
        loadingText: t('fundsTransferPage.Forms.GenericDrawer.LoadingText.City'),
        properties: {
          mt: 3,
          forcePopupIcons: true,
          noOptionsText: t('fundsTransferPage.Forms.AddBankDetails.NoOptions.Message'),
        },
      },
      {
        label: t('fundsTransferPage.Forms.GenericDrawer.Bank'),
        key: ValueIdentifier.BANK_NAME,
        type: 'autocomplete',
        options: bankNamesOptions,
        defaultValue: null,
        externalOnChangeHandler: onChangeHandler,
        disabled: isLoadingSwiftCodes || !isBankCountry || !isBankCity,
        loading: isLoadingSwiftBanks,
        loadingText: t('fundsTransferPage.Forms.GenericDrawer.LoadingText.Bank'),
        properties: {
          mt: 3,
          forcePopupIcons: true,
          noOptionsText: t('fundsTransferPage.Forms.AddBankDetails.NoOptions.Message'),
        },
      },
      {
        type: 'dynamicComponent',
        key: 'dynamicComponent',
        properties: {
          component: (props: any) => (
            <Box width='100%' display='flex' flexDirection='column' flex={1}>
              {isLoadingSwiftCodes && (
                <Box className={classes.loaderContainer}>
                  <ECOMMBXLoader size='small' verticalSpacing='small' />
                </Box>
              )}
              <Button
                fullWidth
                color='primary'
                disabled={isLoadingSwiftCodes}
                size='large'
                variant='contained'
                classes={{ root: clsx(classes.marginButton, classes.submitButton) }}
                title={t('fundsTransferPage.Forms.GenericDrawer.ApplyBtn')}
                type='submit'>
                {t('fundsTransferPage.Forms.GenericDrawer.ApplyBtn')}
              </Button>
            </Box>
          ),
        },
      },
    ],
    preloadedData: unknownBankDetails ?? {
      [ValueIdentifier.BANK_COUNTRY]: null,
      [ValueIdentifier.BANK_CITY]: null,
      [ValueIdentifier.BANK_NAME]: null,
    },
    preloadedDataValidationOptions: { shouldValidate: false, shouldDirty: true },
  };
};

export { useBicSwiftsConfig };
